/**
 * Administration → Shared links (1.6.3), for administrators: every share link in the workspace, active, expired and
 * revoked, with its views, visitors and last view. A row opens its view history (time, IP address, browser); links can
 * be revoked one at a time or several at once.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, Link2, Search, ShieldOff } from 'lucide-react';
import type { AdminShareDTO, AdminShareSort, AdminShareStateFilter, ApprovalStatus } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useAdminShares, useRevokeShares } from '../../api/hooks';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';
import { LoadMore } from '../../components/LoadMore';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import { RowMenu } from '../../components/RowMenu';
import { ShareViewsModal, historyNote } from '../../components/ShareViewsModal';
import { dateTime, relative } from '../../lib/format';

const STATES: Array<{ key: AdminShareStateFilter; label: string }> = [
  { key: 'active', label: 'Active' }, { key: 'expired', label: 'Expired' }, { key: 'revoked', label: 'Revoked' }, { key: 'all', label: 'All' },
];
const STATE_LABEL = { active: 'Active', expired: 'Expired', revoked: 'Revoked' } as const;
const APPROVAL_LABEL: Record<ApprovalStatus, string> = {
  pending: 'Waiting', approved: 'Approved', rejected: 'Changes requested', cancelled: 'Cancelled', expired: 'Expired',
};

export function SharedLinksAdmin() {
  const [state, setState] = useState<AdminShareStateFilter>('active');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<AdminShareSort>('last_view');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState<AdminShareDTO | null>(null);
  const [confirm, setConfirm] = useState<AdminShareDTO[] | null>(null);
  // The search runs when typing pauses.
  useEffect(() => { const t = setTimeout(() => setQ(search), 250); return () => clearTimeout(t); }, [search]);
  const list = useAdminShares({ state, q, sort });
  const revoke = useRevokeShares();
  const navigate = useNavigate();
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const counts = list.data?.counts;
  // Only links still on the page and revocable stay selected.
  const picked = items.filter((s) => selected.has(s.id) && s.canRevoke);
  const revocable = items.filter((s) => s.canRevoke);
  const allPicked = revocable.length > 0 && revocable.every((s) => selected.has(s.id));
  useEffect(() => { setSelected(new Set()); }, [state, q]);

  const toggle = (id: string) => setSelected((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const doRevoke = (links: AdminShareDTO[]) => {
    revoke.mutate(links.map((s) => s.id), {
      onSuccess: (r) => {
        toast(`${r.revoked.length} link${r.revoked.length === 1 ? '' : 's'} revoked${r.skipped.length ? `; ${r.skipped.length} already expired or revoked` : ''}`);
        setSelected(new Set());
        setConfirm(null);
      },
      onError: (e) => toast(errorMessage(e), 'error'),
    });
  };

  return (
    <div className="page" data-testid="shared-links-admin">
      <p className="lede">Every share link in this workspace, on every asset. Revoking a link stops it at once, also for anyone watching now.</p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <div role="group" aria-label="State" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATES.map((s) => (
            <button key={s.key} type="button" className={`chip ${state === s.key ? 'active' : ''}`} aria-pressed={state === s.key} onClick={() => setState(s.key)} data-testid={`shares-state-${s.key}`}>
              {s.label}{counts ? ` ${counts[s.key].toLocaleString()}` : ''}
            </button>
          ))}
        </div>
        <div className="search" role="search" style={{ flex: '1 1 220px', maxWidth: 360 }}>
          <Search size={15} />
          <input className="input" placeholder="Search asset, code or maker…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search shared links" data-testid="shares-search" />
        </div>
        <select className="select" value={sort} onChange={(e) => setSort(e.target.value as AdminShareSort)} aria-label="Order" style={{ width: 'auto', flex: 'none' }} data-testid="shares-sort">
          <option value="last_view">Last viewed</option>
          <option value="created">Newest</option>
        </select>
      </div>
      <p className="faint" style={{ margin: '0 0 12px', fontSize: 12.5 }} data-testid="shares-history-note">{historyNote(list.data?.historyDays)}</p>
      {picked.length > 0 && (
        <div className="selection-bar" role="toolbar" aria-label="Selected links" data-testid="shares-selection" style={{ borderRadius: 8, marginBottom: 8 }}>
          <span className="selection-count">{picked.length} selected</span>
          <button className="btn ghost sm" onClick={() => setSelected(new Set())}>Clear</button>
          <div className="selection-actions">
            <button className="btn sm danger" onClick={() => setConfirm(picked)} disabled={revoke.isPending} data-testid="shares-revoke-selected"><ShieldOff size={13} /> Revoke selected</button>
          </div>
        </div>
      )}
      {list.isLoading ? <div className="skeleton" style={{ height: 160 }} /> : items.length === 0 ? (
        <EmptyState style={{ minHeight: 200 }} icon={<Link2 size={24} />} title="No shared links" testId="shares-empty">
          <p>{q ? 'No link matches this search.' : state === 'all' ? 'Links made with Share on an asset appear here.' : `No ${STATE_LABEL[state].toLowerCase()} links.`}</p>
        </EmptyState>
      ) : (
        <TableScroll>
          <table className="table" aria-label="Shared links" data-testid="shares-table">
            <thead><tr>
              <th className="select-col"><input type="checkbox" className="select-check" aria-label="Select all links that can be revoked" checked={allPicked} disabled={!revocable.length}
                onChange={() => setSelected(allPicked ? new Set() : new Set(revocable.map((s) => s.id)))} data-testid="shares-select-all" /></th>
              <th style={{ width: 76 }} aria-label="Preview" /><th>Asset</th><th>Link</th><th>Made by</th><th>Created</th><th>Expires</th><th>State</th>
              <th>Download</th><th>Approval</th><th>Views</th><th>Visitors</th><th>Last view</th><th aria-label="Actions" />
            </tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid="share-admin-row" data-share-id={s.id} data-state={s.state} tabIndex={0} aria-selected={selected.has(s.id)}
                  onClick={() => setDetails(s)} onKeyDown={(e) => { if (e.key === 'Enter') setDetails(s); }}>
                  <td className="select-col" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="select-check" aria-label={`Select …${s.tokenHint}`} disabled={!s.canRevoke} checked={selected.has(s.id) && s.canRevoke}
                      onChange={() => toggle(s.id)} data-testid="share-admin-select" />
                  </td>
                  <td>{s.asset.thumbnailUrl ? <img className="mini-thumb" src={s.asset.thumbnailUrl} alt="" loading="lazy" /> : <div className="mini-thumb" />}</td>
                  <td className="wrap" style={{ minWidth: 160 }}>
                    <Link to={`/assets/${s.asset.id}?tab=sharing`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 550 }}>{s.asset.title}</Link>
                    <div className="faint mono" style={{ fontSize: 11.5 }}>{s.asset.code}</div>
                  </td>
                  <td className="mono nowrap" title="The start of the link">…{s.tokenHint}</td>
                  <td className="muted wrap">{s.createdBy ?? '—'}</td>
                  <td className="muted nowrap" title={dateTime(s.createdAt)}>{relative(s.createdAt)}</td>
                  <td className="muted nowrap" title={s.expiresAt ? dateTime(s.expiresAt) : undefined}>{s.expiresAt ? dateTime(s.expiresAt) : 'Never'}</td>
                  <td className="nowrap"><span className={`pill ${s.state === 'active' ? 'accent' : ''}`} data-testid="share-admin-state"
                    title={s.state === 'revoked' ? `Revoked${s.revokedBy ? ` by ${s.revokedBy}` : ''} ${dateTime(s.revokedAt)}` : undefined}>{STATE_LABEL[s.state]}</span></td>
                  <td className="muted nowrap">{s.allowDownload ? <span title="Allows downloading the proxy"><Download size={12} /> Yes</span> : 'No'}</td>
                  <td className="muted nowrap">{s.approval ? APPROVAL_LABEL[s.approval.status] : '—'}</td>
                  <td className="nowrap" data-testid="share-admin-views">{s.views.toLocaleString()}</td>
                  <td className="nowrap" data-testid="share-admin-visitors">{s.visitors.toLocaleString()}</td>
                  <td className="muted nowrap" title={s.lastViewedAt ? dateTime(s.lastViewedAt) : undefined}>{s.lastViewedAt ? relative(s.lastViewedAt) : 'Never'}</td>
                  <td className="actions" onClick={(e) => e.stopPropagation()}><div>
                    <RowMenu label={`Actions for …${s.tokenHint}`} testId="share-admin-menu" items={[
                      { key: 'views', label: 'View history', onSelect: () => setDetails(s) },
                      ...(s.canRevoke ? [{ key: 'revoke', label: 'Revoke', danger: true, onSelect: () => setConfirm([s]) }] : []),
                      { key: 'asset', label: 'Open asset', onSelect: () => navigate(`/assets/${s.asset.id}?tab=sharing`) },
                    ]} />
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <LoadMore list={list} shown={items.length} noun="links" />
      {details && <ShareViewsModal share={details} onClose={() => setDetails(null)} />}
      {confirm && (
        <Modal title={confirm.length === 1 ? `Revoke …${confirm[0]!.tokenHint}?` : `Revoke ${confirm.length} links?`} onClose={() => setConfirm(null)}
          actions={<>
            <button className="btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button className="btn danger" disabled={revoke.isPending} onClick={() => doRevoke(confirm)} data-testid="shares-revoke-confirm"><ShieldOff size={13} /> Revoke</button>
          </>}>
          <p>{confirm.length === 1 ? `The link to “${confirm[0]!.asset.title}” stops working at once` : `These ${confirm.length} links stop working at once`}, also for anyone watching now. This cannot be undone; a new link can be made from the asset.</p>
        </Modal>
      )}
    </div>
  );
}
